import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { MapPin, Phone, Mail, Clock, Headphones, X, PhoneCall } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Branches = () => {
  const [locations, setLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // HELPER: Yeh function normal links ya address ko Embed format mein badal dega
  const getMapEmbedUrl = (input) => {
    if (!input) return "";

    // Agar link mein 'pb=' hai (yani pehle se embed link hai)
    if (input.includes('pb=')) return input;

    // Agar normal Google Map link hai (WhatsApp wala), toh usme se query extract karo
    let query = input;
    try {
      if (input.includes('maps/place/')) {
        query = input.split('maps/place/')[1].split('/')[0];
      } else if (input.includes('?q=')) {
        const urlObj = new URL(input);
        query = urlObj.searchParams.get('q');
      }
    } catch (e) {
      query = input; // Fallback to raw input
    }

    // Google Maps Embed API (Free version using search query)
    return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        
        setLocations(data);
        if (data && data.length > 0) {
          setActiveTab(data[0].id);
        }
      } catch (err) {
        console.error("Error fetching branches:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
  }, []);

  const current = locations.find(l => l.id === activeTab);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-black uppercase tracking-widest text-blue-600">
        Loading Network...
      </div>
    );
  }

  if (locations.length === 0) return null;

  return (
    <section className="py-24 px-4 bg-white max-w-7xl mx-auto pt-32 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
        <div>
          <p className="text-blue-600 font-bold text-xs tracking-widest uppercase mb-2">Our Presence</p>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter">
            Our Strategic <span className="text-blue-600 italic font-medium lowercase">Network</span>
          </h2>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 bg-blue-50 px-5 py-3 rounded-none text-blue-600 font-black text-xs border border-blue-100 shadow-sm">
            <MapPin size={16}/> PAN INDIA
          </div>
          <button 
            onClick={() => setShowSupportModal(true)} 
            className="flex items-center gap-2 bg-slate-900 px-5 py-3 rounded-none text-white font-black text-xs shadow-lg hover:bg-blue-600 transition-all uppercase"
          >
            <Headphones size={16}/> 24/7 Support
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <div className="space-y-2 overflow-y-auto max-h-[600px] pr-2 scrollbar-hide">
            {locations.map((loc) => (
              <button 
                key={loc.id} 
                onClick={() => setActiveTab(loc.id)}
                className={`w-full flex items-center gap-4 p-5 rounded-none border transition-all duration-300 ${
                  activeTab === loc.id 
                  ? 'bg-blue-600 border-blue-600 text-white shadow-xl translate-x-1' 
                  : 'bg-gray-50 border-gray-100 text-slate-600 hover:bg-white'
                }`}
              >
                <MapPin size={18} className={activeTab === loc.id ? 'text-white' : 'text-blue-600'} />
                <div className="text-left">
                  <p className="font-black text-sm uppercase tracking-tight leading-none">{loc.name}</p>
                </div>
              </button>
            ))}
          </div>

          <div 
            onClick={() => setShowSupportModal(true)}
            className="p-8 bg-[#0a1128] rounded-none text-white relative overflow-hidden group cursor-pointer border border-white/10 hover:border-blue-500 transition-all"
          >
            <Headphones className="text-blue-500 mb-6" size={32} />
            <h4 className="text-xl font-black uppercase tracking-tight">24/7 Dedicated Support</h4>
            <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest italic leading-relaxed">Centralized helpdesk for emergency travel needs across all branches.</p>
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-8 bg-white border border-gray-100 shadow-2xl rounded-none overflow-hidden flex flex-col">
          <div className="p-10 flex-grow">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
              <div>
                <p className="text-blue-600 font-bold text-[10px] uppercase tracking-[0.3em] mb-2">Location Profile</p>
                <h3 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tighter uppercase leading-none">{current.name}</h3>
              </div>
              <a 
                href={`mailto:${current.email}`} 
                className="inline-block text-center bg-blue-600 text-white px-8 py-3 rounded-none font-black text-xs tracking-widest hover:bg-slate-900 transition-all uppercase"
              >
                Send Enquiry
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-6">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <MapPin size={20} className="text-blue-600 shrink-0" />
                  <p className="text-xs font-bold text-slate-600 leading-relaxed uppercase">{current.address}</p>
                </div>
                <div className="flex gap-4">
                  <Phone size={20} className="text-blue-600 shrink-0" />
                  <div>
                    <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Direct Line</p>
                    <p className="text-sm font-black text-slate-900">{current.phone}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <Mail size={20} className="text-blue-600 shrink-0" />
                  <div>
                    <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Official Email</p>
                    <p className="text-sm font-black text-slate-900">{current.email}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock size={20} className="text-blue-600 shrink-0" />
                  <div>
                    <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Status</p>
                    <p className="text-sm font-black text-green-600 uppercase">Operational 24/7</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map Display (NOW SUPPORTS NORMAL LINKS) */}
          <div className="h-[400px] w-full bg-gray-100 grayscale hover:grayscale-0 transition-all duration-700 border-t">
            <iframe 
              title={current.name} 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              src={getMapEmbedUrl(current.map_url)} 
              allowFullScreen="" 
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Support Modal */}
      <AnimatePresence>
        {showSupportModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => setShowSupportModal(false)} />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative bg-white w-full max-w-md p-10 rounded-none shadow-2xl text-center border-t-4 border-blue-600">
              <button onClick={() => setShowSupportModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"><X size={24}/></button>
              <div className="w-16 h-16 bg-blue-50 flex items-center justify-center mx-auto mb-6"><Headphones size={32} className="text-blue-600" /></div>
              <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-2">Emergency Helpdesk</h3>
              <p className="text-slate-500 text-xs mb-8 font-medium">Connect with our specialized team for immediate assistance across India.</p>
              <a href="tel:+919791111275" className="flex items-center justify-center gap-3 w-full bg-blue-600 text-white py-4 rounded-none font-black text-xs uppercase tracking-widest hover:bg-slate-900 shadow-xl transition-all">
                <PhoneCall size={18} /> Call Helpline Now
              </a>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Branches;